import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import persistReducer from "redux-persist/es/persistReducer";
import {
  CREATE_STATE_URL,
  DELETE_STATE_URL,
  STATE_EXPORT_URL,
  STATE_IMPORT_URL,
  STATE_LIST_URL,
  STATE_SAMPLE_URL,
  UPDATE_STATE_URL,
} from "../../Services/endpoints";
import { errorHandler, successToast } from "../../Utils/toast";
import {
  _delete,
  _get,
  _getBlob,
  _patch,
  _post,
  _postFile,
} from "../../Services/baseApi";
import {
  downloadBlobFile,
  getFilenameFromDisposition,
} from "../../Utils/downloadFile";
import storage from "redux-persist/lib/storage";

const initialValue = {
  isLoading: false,
  states: [],
  total: 0,
  page: 1,
  limit: 10,
};

const stateSlice = createSlice({
  name: "state",
  initialState: initialValue,
  reducers: {
    setStates: (state, action) => {
      state.states = action?.payload;
    },
  },
  extraReducers: (builder) => {
    // CREATE STATE
    builder.addCase(createStateThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createStateThunk.fulfilled, (state, action) => {
      state.states = [action?.payload?.result, ...state.states];
      state.isLoading = false;
    });
    builder.addCase(createStateThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Create State Error:", action?.payload);
    });

    // GET STATES
    builder.addCase(getStatesThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getStatesThunk.fulfilled, (state, action) => {
      state.states = action?.payload?.result?.data || [];
      state.total = action?.payload?.result?.meta?.total;
      state.page = action?.payload?.result?.meta?.page;
      state.limit = action?.payload?.result?.meta?.limit;
      state.isLoading = false;
    });
    builder.addCase(getStatesThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Get States Error:", action?.payload);
    });

    // UPDATE STATE
    builder.addCase(updateStateThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateStateThunk.fulfilled, (state, action) => {
      state.states = state.states.map((item) =>
        item.id === action?.payload?.id ? action?.payload : item
      );
      state.isLoading = false;
    });
    builder.addCase(updateStateThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Update State Error:", action?.payload);
    });

    // DELETE STATE
    builder.addCase(deleteStateThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteStateThunk.fulfilled, (state, action) => {
      state.states = state.states.filter((item) => item.id !== action?.payload);
      state.isLoading = false;
    });
    builder.addCase(deleteStateThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Delete State Error:", action?.payload);
    });
  },
});

export default persistReducer({ key: "state", storage }, stateSlice.reducer);

// ------------------- Thunks -------------------

export const createStateThunk = createAsyncThunk(
  "stateSlice/createStateThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _post(CREATE_STATE_URL, {
        name: payload?.name || undefined,
      });
      if (response?.data) {
        successToast(response.data.message || "State created successfully");
        payload?.handleFun();
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getStatesThunk = createAsyncThunk(
  "stateSlice/getStatesThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(STATE_LIST_URL, {
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        searchText: payload?.searchText || undefined,
        sortKey: payload?.sortColumn || undefined,
        sortOrder: payload?.sortDirection || undefined,
        searchKey:
          payload?.searchKey || payload?.searchText ? "name" : undefined,
      });
      if (response?.data) {
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStateThunk = createAsyncThunk(
  "stateSlice/updateStateThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _patch(UPDATE_STATE_URL, {
        id: payload?.id || undefined,
        name: payload?.name || undefined,
      });
      if (response?.data) {
        successToast(response.data.message || "State updated successfully");
        payload?.handleFun();
        return response.data.result;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStateThunk = createAsyncThunk(
  "stateSlice/deleteStateThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _delete(DELETE_STATE_URL + payload?.id);
      if (response?.data) {
        successToast(response.data.message || "State deleted successfully");
        payload?.handleFun();
        return payload?.id; // return id for reducer
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportStatesThunk = createAsyncThunk(
  "stateSlice/exportStatesThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _getBlob(STATE_EXPORT_URL, {
        searchText: payload?.searchText || undefined,
        searchKey: payload?.searchText ? "name" : undefined,
      });
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "states-export.xlsx"
      );
      downloadBlobFile(response.data, filename);
      successToast("States exported successfully");
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadStateSampleThunk = createAsyncThunk(
  "stateSlice/downloadStateSampleThunk",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _getBlob(STATE_SAMPLE_URL);
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "states-sample.xlsx"
      );
      downloadBlobFile(response.data, filename);
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const importStatesThunk = createAsyncThunk(
  "stateSlice/importStatesThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", payload.file);
      const response = await _postFile(STATE_IMPORT_URL, formData);
      if (response?.data) {
        const result = response.data?.result;
        if (result?.imported > 0) {
          successToast(
            response.data.message || "States imported successfully"
          );
          payload?.handleFun?.(result);
        } else {
          errorHandler({
            response: {
              data: {
                message:
                  response.data.message ||
                  "No states were imported. Please check the file and try again.",
              },
            },
          });
        }
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
